import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import {
  ChatResponse,
  RecipientCountItem,
  clientsendMessage,
  getClientChat,
  getPreChat,
  getPreChatHistory,
  getProjectChat,
  sendMessage,
  sendPreChatMessage,
  transferPreChatToProject,
} from '@/api/projectChat';

import { MessageData } from '@/interface';

const toErrorMessage = (error: unknown, fallbackMessage: string): string => {
  const message = axios.isAxiosError(error) ? (error.response?.data as { message?: string } | undefined)?.message : undefined;
  return message || fallbackMessage;
};

export type ActiveNotificationContext =
  | {
      type: 'preChat';
      clientId?: string;
      tenantId?: string;
      projectId?: string;
    }
  | {
      type: 'projectChat';
      projectId?: string;
    }
  | null;

interface ProjectChatState {
  connected: boolean;
  loading: boolean;
  error: string | null;
  messages: {
    currentUser: { currentUserId: string };
    data: MessageData[];
  };
  clientMessages: {
    currentUser: { currentUserId: string };
    data: MessageData[];
  };
  preChatMessages: {
    currentUser: { clientId: string };
    data: MessageData[];
  };

  preChatLoading: {
    initialLoad: boolean;
    historyLoad: boolean;
    sendingMessage: boolean;
  };

  projectChatLoading: {
    initialLoad: boolean;
  };

  typingUsers: string[] | undefined;
  hasUnreadFindProject: boolean;
  findProjectUnseenCounts: Record<string, number>;
  hasUnreadSuggestedTenant: boolean;
  suggestedTenantUnseenCounts: Record<string, number>;
  suggestedTenantUnseenCountsByProject: Record<string, Record<string, number>>;
  projectChatUnseenCount: number;
  projectChatUnseenCountsByProject: Record<string, number>;
  hasUnreadProjectChat: boolean;
  activeNotificationContext: ActiveNotificationContext;
  expertStatusMessagesByProject: Record<string, MessageData[]>;
}

const initialState: ProjectChatState = {
  connected: false,
  loading: false,
  error: null,
  messages: {
    currentUser: { currentUserId: '' },
    data: [],
  },
  clientMessages: {
    currentUser: { currentUserId: '' },
    data: [],
  },
  preChatMessages: {
    currentUser: { clientId: '' },
    data: [],
  },
  preChatLoading: {
    initialLoad: false,
    historyLoad: false,
    sendingMessage: false,
  },
  projectChatLoading: {
    initialLoad: false,
  },
  typingUsers: [],
  hasUnreadFindProject: false,
  findProjectUnseenCounts: {},
  hasUnreadSuggestedTenant: false,
  suggestedTenantUnseenCounts: {},
  suggestedTenantUnseenCountsByProject: {},
  projectChatUnseenCount: 0,
  projectChatUnseenCountsByProject: {},
  hasUnreadProjectChat: false,
  activeNotificationContext: null,
  expertStatusMessagesByProject: {},
};
// export const connectToProjectSocket = createAsyncThunk(
//   'projectChat/connect',
//   async ({ projectId, token }: { projectId: string; token?: string }, { rejectWithValue }) => {
//     try {
//       await connectSocket(projectId, token);
//     } catch (err: any) {
//       return rejectWithValue(err);
//     }
//   },
// );
//
// export const disconnectFromProjectSocket = createAsyncThunk('projectChat/disconnect', async () => {
//   disconnectSocket();
// });

export const sendProjectMessage = createAsyncThunk(
  'projectChat/sendMessage',
  async ({
    projectId,
    content,
    replyToMessageId,
    attachments,
  }: {
    projectId: string;
    content: string;
    replyToMessageId?: string;
    attachments?: string[];
  }) => {
    await sendMessage({ projectId, content, replyToMessageId, attachments });
  },
);
export const clientsendMessageRequest = createAsyncThunk(
  'projectChat/clientsendMessage',
  async ({
    projectId,
    content,
    replyToMessageId,
    attachments,
  }: {
    projectId: string;
    content: string;
    replyToMessageId?: string;
    attachments?: string[];
  }) => {
    await clientsendMessage({ projectId, content, replyToMessageId, attachments });
  },
);

export const getProjectChatRequest = createAsyncThunk<ChatResponse, string, { rejectValue: string }>(
  'chat/projectchat',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await getProjectChat(projectId);
      return response.data;
    } catch (error) {
      return rejectWithValue(toErrorMessage(error, 'Failed to fetch project chat'));
    }
  },
);

export const getClientChatRequest = createAsyncThunk<ChatResponse, { projectId: string; token: string }, { rejectValue: string }>(
  'chat/getClientChatRequest',
  async ({ projectId, token }, { rejectWithValue }) => {
    try {
      const response = await getClientChat(projectId, token);
      return response.data;
    } catch (error) {
      return rejectWithValue(toErrorMessage(error, 'Failed to fetch client chat'));
    }
  },
);

export const getPreChatRequest = createAsyncThunk<MessageData[], { clientId: string; token: string }, { rejectValue: string }>(
  'preChat/getPreChatRequest',
  async ({ clientId, token }, { rejectWithValue }) => {
    try {
      const response = await getPreChat(clientId, token);
      return response.data;
    } catch (error) {
      return rejectWithValue(toErrorMessage(error, 'Failed to fetch pre-chat'));
    }
  },
);

export const sendPreChatMessageRequest = createAsyncThunk<
  unknown,
  {
    clientId: string;
    content: string;
    attachments?: string[];
    tenantId?: string;
    projectId?: string;
    replyToMessageId?: string;
  },
  { rejectValue: string }
>('preChat/sendPreChatMessageRequest', async (payload, { rejectWithValue }) => {
  try {
    const res = await sendPreChatMessage(payload);
    return res.data;
  } catch (error) {
    return rejectWithValue(toErrorMessage(error, 'Failed to send message'));
  }
});

export const getPreChatHistoryRequest = createAsyncThunk<
  MessageData[],
  { clientId: string; token: string; tenantId?: string; projectId?: string },
  { rejectValue: string }
>('preChat/getPreChatHistoryRequest', async ({ clientId, token, tenantId, projectId }, { rejectWithValue }) => {
  try {
    const res = await getPreChatHistory(clientId, token, tenantId, projectId);
    return res.data;
  } catch (error) {
    return rejectWithValue(toErrorMessage(error, 'Failed to fetch pre-chat history'));
  }
});

export const transferPreChatToProjectRequest = createAsyncThunk<
  unknown,
  { projectId: string; clientId: string; companyId?: string; token?: string },
  { rejectValue: string }
>('preChat/transferPreChatToProjectRequest', async ({ projectId, clientId, companyId, token }, { rejectWithValue }) => {
  try {
    const res = await transferPreChatToProject(projectId, clientId, companyId, token);
    return res.data; // { message, projectId }
  } catch (error) {
    return rejectWithValue(toErrorMessage(error, 'Failed to transfer pre-chat'));
  }
});

export const projectChatSlice = createSlice({
  name: 'projectChat',
  initialState,
  reducers: {
    receiveMessage: (state, action: PayloadAction<MessageData>) => {
      state.messages.data.push(action.payload);
    },

    clearMessages: (state) => {
      state.messages.data = [];
      state.clientMessages.data = [];
      state.preChatMessages.data = [];
    },
    setPreChatInitialLoading: (state, action: PayloadAction<boolean>) => {
      state.preChatLoading.initialLoad = action.payload;
    },
    setPreChatHistoryLoading: (state, action: PayloadAction<boolean>) => {
      state.preChatLoading.historyLoad = action.payload;
    },
    setPreChatSendingMessage: (state, action: PayloadAction<boolean>) => {
      state.preChatLoading.sendingMessage = action.payload;
    },
    setProjectChatInitialLoading: (state, action: PayloadAction<boolean>) => {
      state.projectChatLoading.initialLoad = action.payload;
    },
    addMessage: (state, action) => {
      if (!state.messages.data.some((msg) => msg.id === action.payload.id)) {
        state.messages.data.push(action.payload);
      }
    },
    addClientMessage: (state, action) => {
      const existingMessage = state.clientMessages.data.find((msg) => msg.id === action.payload.id);
      if (existingMessage) {
        Object.assign(existingMessage, action.payload);
      } else {
        state.clientMessages.data.push(action.payload);
      }
    },
    addPreChatMessage: (state, action) => {
      if (!state.preChatMessages.data.some((msg) => msg.id === action.payload.id)) {
        state.preChatMessages.data.push(action.payload);
      }
    },
    setTypingUser: (state, action) => {
      const username = action.payload;
      if (!state.typingUsers?.includes(username)) {
        state.typingUsers?.push(username);
      }
    },
    setStopTypingUser: (state, action) => {
      const username = action.payload;
      state.typingUsers = state.typingUsers?.filter((u) => u !== username);
    },
    setHasUnreadFindProject: (state, action: PayloadAction<boolean>) => {
      state.hasUnreadFindProject = action.payload;
    },
    setFindProjectUnseenCounts: (state, action: PayloadAction<Record<string, number>>) => {
      state.findProjectUnseenCounts = action.payload;
      state.hasUnreadFindProject = Object.values(action.payload).some((count) => count > 0);
    },
    setHasUnreadSuggestedTenant: (state, action: PayloadAction<boolean>) => {
      state.hasUnreadSuggestedTenant = action.payload;
    },
    setSuggestedTenantUnseenCounts: (state, action: PayloadAction<Record<string, number>>) => {
      state.suggestedTenantUnseenCounts = action.payload;
    },
    setRecipientCountsSnapshot: (
      state,
      action: PayloadAction<{ preChatCounts?: RecipientCountItem[]; projectChatCounts?: RecipientCountItem[] }>,
    ) => {
      const findProjectCounts: Record<string, number> = {};
      const suggestedTenantCountsByProject: Record<string, Record<string, number>> = {};
      const projectChatCountsByProject: Record<string, number> = {};

      (action.payload.preChatCounts || []).forEach((countItem) => {
        if (!countItem?.projectId) return;

        const count = Math.max(0, Number(countItem.count) || 0);
        findProjectCounts[countItem.projectId] = (findProjectCounts[countItem.projectId] || 0) + count;

        if (countItem.tenantId) {
          suggestedTenantCountsByProject[countItem.projectId] = {
            ...(suggestedTenantCountsByProject[countItem.projectId] || {}),
            [countItem.tenantId]: count,
          };
        }
      });

      (action.payload.projectChatCounts || []).forEach((countItem) => {
        if (!countItem?.projectId) return;
        projectChatCountsByProject[countItem.projectId] = Math.max(0, Number(countItem.count) || 0);
      });

      state.findProjectUnseenCounts = findProjectCounts;
      state.hasUnreadFindProject = Object.values(findProjectCounts).some((count) => count > 0);
      state.suggestedTenantUnseenCountsByProject = suggestedTenantCountsByProject;
      state.projectChatUnseenCountsByProject = projectChatCountsByProject;
    },
    setPreChatUnseenCountForProject: (state, action: PayloadAction<{ projectId?: string; tenantId?: string; count: number }>) => {
      const { projectId, tenantId } = action.payload;
      if (!projectId) return;

      const count = Math.max(0, Number(action.payload.count) || 0);

      if (tenantId) {
        state.suggestedTenantUnseenCountsByProject[projectId] = {
          ...(state.suggestedTenantUnseenCountsByProject[projectId] || {}),
          [tenantId]: count,
        };
        state.findProjectUnseenCounts[projectId] = Object.values(state.suggestedTenantUnseenCountsByProject[projectId]).reduce(
          (sum, itemCount) => sum + (Number(itemCount) || 0),
          0,
        );
      } else {
        state.findProjectUnseenCounts[projectId] = count;
      }

      state.hasUnreadFindProject = Object.values(state.findProjectUnseenCounts).some((itemCount) => itemCount > 0);
    },
    setProjectChatUnseenCount: (state, action: PayloadAction<number>) => {
      state.projectChatUnseenCount = action.payload;
      state.hasUnreadProjectChat = action.payload > 0;
    },
    setProjectChatUnseenCountForProject: (state, action: PayloadAction<{ projectId?: string; count: number }>) => {
      if (!action.payload.projectId) return;
      state.projectChatUnseenCountsByProject[action.payload.projectId] = Math.max(0, Number(action.payload.count) || 0);
    },
    setHasUnreadProjectChat: (state, action: PayloadAction<boolean>) => {
      state.hasUnreadProjectChat = action.payload;
    },
    setActiveNotificationContext: (state, action: PayloadAction<ActiveNotificationContext>) => {
      state.activeNotificationContext = action.payload;
    },
    clearActiveNotificationContext: (state) => {
      state.activeNotificationContext = null;
    },
    addExpertStatusMessage: (
      state,
      action: PayloadAction<{ projectId: string; message: MessageData; target: 'team' | 'client' }>,
    ) => {
      const { projectId, message, target } = action.payload;
      const projectMessages = state.expertStatusMessagesByProject[projectId] || [];
      if (!projectMessages.some((msg) => msg.id === message.id)) {
        state.expertStatusMessagesByProject[projectId] = [...projectMessages, message];
      }

      if (target === 'team') {
        if (!state.messages.data.some((msg) => msg.id === message.id)) {
          state.messages.data.push(message);
        }
      } else {
        if (!state.clientMessages.data.some((msg) => msg.id === message.id)) {
          state.clientMessages.data.push(message);
        }
      }
    },
  },
  extraReducers: (builder) => {
    // builder
    //   .addCase(connectToProjectSocket.pending, (state) => {
    //     state.loading = true;
    //   })
    //   .addCase(connectToProjectSocket.fulfilled, (state) => {
    //     state.connected = true;
    //     state.error = null;
    //     state.loading = false;
    //   })
    //   .addCase(connectToProjectSocket.rejected, (state, action) => {
    //     state.connected = false;
    //     state.error = action.payload;
    //     state.loading = false;
    //   })
    //   .addCase(disconnectFromProjectSocket.fulfilled, (state) => {
    //     state.connected = false;
    //     state.error = null;
    //     state.messages.data = [];
    //   });

    builder
      .addCase(sendProjectMessage.pending, (state) => {
        state.loading = true;
      })
      .addCase(sendProjectMessage.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sendProjectMessage.rejected, (state, action) => {
        state.error = (action.payload as string | undefined) ?? null;
        state.loading = false;
      })
      .addCase(clientsendMessageRequest.pending, (state) => {
        state.loading = true;
      })
      .addCase(clientsendMessageRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(clientsendMessageRequest.rejected, (state, action) => {
        state.error = (action.payload as string | undefined) ?? null;
        state.loading = false;
      });

    builder
      .addCase(getProjectChatRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.projectChatLoading.initialLoad = true;
      })
      .addCase(getProjectChatRequest.fulfilled, (state, action) => {
        const currentUserId = action.payload.currentUser?.currentUserId;
        const projectId = action.meta.arg;
        const messagesWithFlag = action.payload.data.map((msg: MessageData) => ({
          ...msg,
          isSentByMe: msg.isSentByMe !== undefined ? msg.isSentByMe : msg.userInfo?.id === currentUserId,
        }));
        const persistedExpertStatusMessages = state.expertStatusMessagesByProject[projectId] || [];
        const mergedData = [...messagesWithFlag];
        persistedExpertStatusMessages.forEach((statusMsg) => {
          if (!mergedData.some((msg) => msg.id === statusMsg.id)) {
            mergedData.push(statusMsg);
          }
        });
        mergedData.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        state.messages = {
          ...action.payload,
          data: mergedData,
        };
        state.loading = false;
        state.projectChatLoading.initialLoad = false;
      })
      .addCase(getProjectChatRequest.rejected, (state, action) => {
        state.error = (action.payload as string | undefined) ?? null;
        state.loading = false;
        state.projectChatLoading.initialLoad = false;
      });
    builder
      .addCase(getClientChatRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getClientChatRequest.fulfilled, (state, action) => {
        const currentUserId = action.payload.currentUser?.currentUserId;
        const projectId = action.meta.arg.projectId;
        const messagesWithFlag = action.payload.data.map((msg: MessageData) => ({
          ...msg,
          isSentByMe: msg.isSentByMe !== undefined ? msg.isSentByMe : msg.userInfo?.id === currentUserId,
        }));
        const persistedExpertStatusMessages = state.expertStatusMessagesByProject[projectId] || [];
        const mergedData = [...messagesWithFlag];
        persistedExpertStatusMessages.forEach((statusMsg) => {
          if (!mergedData.some((msg) => msg.id === statusMsg.id)) {
            mergedData.push(statusMsg);
          }
        });
        mergedData.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        state.clientMessages = {
          ...action.payload,
          data: mergedData,
        };
        state.loading = false;
      })
      .addCase(getClientChatRequest.rejected, (state, action) => {
        state.error = (action.payload as string | undefined) ?? null;
        state.loading = false;
      });

    builder
      .addCase(getPreChatRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPreChatRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.preChatMessages.data = action.payload;
      })
      .addCase(getPreChatRequest.rejected, (state, action) => {
        state.error = (action.payload as string | undefined) ?? null;
        state.loading = false;
      })
      .addCase(sendPreChatMessageRequest.pending, (state) => {
        state.preChatLoading.sendingMessage = true;
      })
      .addCase(sendPreChatMessageRequest.fulfilled, (state) => {
        state.preChatLoading.sendingMessage = false;
        // Don't add message here - let socket events handle real-time updates
        // This prevents duplicate messages when socket event is received
      })
      .addCase(sendPreChatMessageRequest.rejected, (state, action) => {
        state.error = (action.payload as string | undefined) ?? null;
        state.preChatLoading.sendingMessage = false;
      })
      .addCase(getPreChatHistoryRequest.pending, (state) => {
        state.preChatLoading.historyLoad = true;
        state.error = null;
      })
      .addCase(getPreChatHistoryRequest.fulfilled, (state, action) => {
        state.preChatLoading.historyLoad = false;
        state.preChatMessages.data = action.payload;
      })
      .addCase(getPreChatHistoryRequest.rejected, (state, action) => {
        state.error = (action.payload as string | undefined) ?? null;
        state.preChatLoading.historyLoad = false;
      })
      .addCase(transferPreChatToProjectRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(transferPreChatToProjectRequest.fulfilled, (state, action) => {
        state.loading = false;
        console.log('✅ PreChat transferred:', action.payload);
      })
      .addCase(transferPreChatToProjectRequest.rejected, (state, action) => {
        state.error = (action.payload as string | undefined) ?? null;
        state.loading = false;
      });
  },
});
export const {
  addMessage,
  addPreChatMessage,
  addClientMessage,
  clearMessages,
  receiveMessage,
  setTypingUser,
  setStopTypingUser,
  setHasUnreadFindProject,
  setFindProjectUnseenCounts,
  setHasUnreadSuggestedTenant,
  setSuggestedTenantUnseenCounts,
  setRecipientCountsSnapshot,
  setPreChatUnseenCountForProject,
  setPreChatInitialLoading,
  setPreChatHistoryLoading,
  setPreChatSendingMessage,
  setProjectChatInitialLoading,
  setProjectChatUnseenCount,
  setProjectChatUnseenCountForProject,
  setHasUnreadProjectChat,
  setActiveNotificationContext,
  clearActiveNotificationContext,
  addExpertStatusMessage,
} = projectChatSlice.actions;

export default projectChatSlice.reducer;
